import React from "react";
import { InlineMath } from "react-katex";
import FractionInput, { formatLatexPreview } from "../../../../components/inputs/FractionInput";

function randInt(min, max) {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

function pick(arr) {
  return arr[Math.floor(Math.random() * arr.length)];
}

function formatNumber(value) {
  return value < 0 ? `(${value})` : `${value}`;
}

function operationLatex(data) {
  if (data.latex) return data.latex;
  return `${formatNumber(data.a)} ${data.op} ${formatNumber(data.b)}`;
}

function binaryResult(data) {
  return data.op === "+" ? data.a + data.b : data.a - data.b;
}

function makeTask(type, data, note) {
  return {
    type,
    data: {
      ...data,
      result: typeof data.result === "number" ? data.result : binaryResult(data),
      note,
    },
    tags: ["liczby_calkowite", "dodawanie_odejmowanie"],
  };
}

function positiveSubtraction() {
  const b = randInt(2, 8);
  const result = randInt(2, 9);
  const a = b + result;
  return makeTask(
    "IntegerStandardSubtractionTask",
    { a, b, op: "-" },
    "To zwykłe odejmowanie: od większej liczby odejmujemy mniejszą."
  );
}

function negativeSubtraction() {
  const a = randInt(2, 8);
  const b = randInt(a + 2, a + 11);
  return makeTask(
    "IntegerNegativeSubtractionTask",
    { a, b, op: "-" },
    "Gdy odejmujemy większą liczbę od mniejszej, wynik jest ujemny."
  );
}

function addNegative() {
  const a = randInt(3, 14);
  const b = -randInt(2, 16);
  return makeTask(
    "IntegerAddNegativeTask",
    { a, b, op: "+" },
    "Dodanie liczby ujemnej działa tak, jak odejmowanie jej wartości."
  );
}

function addTwoNegatives() {
  const x = randInt(2, 12);
  const y = randInt(2, 12);
  return makeTask(
    "IntegerAddTwoNegativesTask",
    { a: -x, b: -y, op: "+" },
    "Dodając dwie liczby ujemne, dodajemy ich odległości od zera, a znak zostaje ujemny."
  );
}

function subtractNegativePositive() {
  const a = randInt(2, 15);
  const x = randInt(2, 15);
  return makeTask(
    "IntegerSubtractNegativePositiveTask",
    { a, b: -x, op: "-" },
    "Odjęcie liczby ujemnej zamienia się w dodanie liczby dodatniej."
  );
}

function subtractNegativeMixed() {
  const aAbs = randInt(4, 18);
  const bAbs = randInt(2, 16);
  return makeTask(
    "IntegerSubtractNegativeMixedTask",
    { a: -aAbs, b: -bAbs, op: "-" },
    "Najpierw zamieniamy odejmowanie liczby ujemnej na dodawanie."
  );
}

function valueForTerm(op, value) {
  return op === "+" ? value : -value;
}

function expressionFromTerms(first, rest) {
  return [
    formatNumber(first),
    ...rest.map(({ op, value }) => `${op}${formatNumber(value)}`),
  ].join("");
}

function normalizedExpressionFromTerms(first, rest) {
  return [
    `${first}`,
    ...rest.map(({ op, value }) => {
      const signed = valueForTerm(op, value);
      return signed >= 0 ? `+${signed}` : `${signed}`;
    }),
  ].join("");
}

function buildChain(first, rest) {
  const original = expressionFromTerms(first, rest);
  const normalized = normalizedExpressionFromTerms(first, rest);
  const parts = [original];
  if (normalized !== original) parts.push(normalized);

  let current = first;
  rest.forEach((term, index) => {
    current += valueForTerm(term.op, term.value);
    const tail = rest
      .slice(index + 1)
      .map(({ op, value }) => {
        const signed = valueForTerm(op, value);
        return signed >= 0 ? `+${signed}` : `${signed}`;
      })
      .join("");
    parts.push(`${current}${tail}`);
  });
  return parts;
}

function randomSignedValue(minAbs, maxAbs) {
  const value = randInt(minAbs, maxAbs);
  return Math.random() < 0.5 ? -value : value;
}

function compositeThreeTerms() {
  const first = randomSignedValue(4, 18);
  const rest = [
    { op: pick(["+", "-"]), value: randomSignedValue(2, 15) },
    { op: pick(["+", "-"]), value: randomSignedValue(2, 15) },
  ];
  if (first > 0 && rest.every(({ value }) => value > 0)) {
    rest[0].value = -Math.abs(rest[0].value);
  }
  const chainParts = buildChain(first, rest);
  const result = Number(chainParts[chainParts.length - 1]);
  return makeTask(
    "IntegerCompositeThreeTermsTask",
    { latex: chainParts[0], chainParts, result },
    "W działaniu z kilkoma składnikami liczymy od lewej do prawej, pilnując znaków."
  );
}

function compositeFourTerms() {
  const first = randomSignedValue(5, 20);
  const rest = [
    { op: pick(["+", "-"]), value: randomSignedValue(2, 18) },
    { op: pick(["+", "-"]), value: randomSignedValue(2, 18) },
    { op: pick(["+", "-"]), value: randomSignedValue(2, 18) },
  ];
  if (first > 0 && rest.every(({ value }) => value > 0)) {
    rest[randInt(0, rest.length - 1)].value *= -1;
  }
  const chainParts = buildChain(first, rest);
  const result = Number(chainParts[chainParts.length - 1]);
  return makeTask(
    "IntegerCompositeFourTermsTask",
    { latex: chainParts[0], chainParts, result },
    "W dłuższym działaniu wygodnie upraszczać zapis krok po kroku od lewej do prawej."
  );
}

export function generateTasks() {
  return [
    positiveSubtraction(),
    negativeSubtraction(),
    addNegative(),
    addTwoNegatives(),
    subtractNegativePositive(),
    subtractNegativeMixed(),
    compositeThreeTerms(),
    compositeFourTerms(),
  ];
}

export function renderTask({ data }) {
  return (
    <p>
      Oblicz: <InlineMath math={operationLatex(data)} />.
    </p>
  );
}

export function renderInput(_task, userAnswer, onChange, disabled) {
  return (
    <FractionInput
      value={userAnswer}
      onChange={onChange}
      disabled={disabled}
      label="wynik"
      placeholder="np. -4"
    />
  );
}

export function checkAnswer({ data }, userAnswer) {
  const value = userAnswer?.parsed;
  return Number.isFinite(value) && value === data.result;
}

export function renderAnswer({ data }) {
  return <InlineMath math={`${data.result}`} />;
}

export function renderUserAnswer(userAnswer) {
  if (!userAnswer || typeof userAnswer !== "object" || !userAnswer.raw) {
    return <i>(brak odpowiedzi)</i>;
  }
  return <InlineMath math={formatLatexPreview(userAnswer.raw)} />;
}

function explanationSteps(type, data) {
  const absA = Math.abs(data.a);
  const absB = Math.abs(data.b);

  if (type === "IntegerStandardSubtractionTask") {
    return [
      <span>
        Odejmujemy jak przy liczbach naturalnych:{" "}
        <InlineMath math={`${data.a}-${data.b}=${data.result}`} />.
      </span>,
    ];
  }

  if (type === "IntegerNegativeSubtractionTask") {
    const diff = data.b - data.a;
    return [
      <span>
        Brakuje <InlineMath math={`${diff}`} /> do liczby{" "}
        <InlineMath math={`${data.b}`} />, więc wynik leży po lewej stronie zera.
      </span>,
      <span>
        Dlatego <InlineMath math={`${data.a}-${data.b}=-${diff}`} />.
      </span>,
    ];
  }

  if (type === "IntegerAddNegativeTask") {
    return [
      <span>
        Zapis <InlineMath math={`${data.a}+(${data.b})`} /> oznacza to samo co{" "}
        <InlineMath math={`${data.a}-${absB}`} />.
      </span>,
      <span>
        Obliczamy <InlineMath math={`${data.a}-${absB}=${data.result}`} />.
      </span>,
    ];
  }

  if (type === "IntegerAddTwoNegativesTask") {
    return [
      <span>
        Liczby <InlineMath math={`${data.a}`} /> i <InlineMath math={`${data.b}`} /> leżą po lewej
        stronie zera.
      </span>,
      <span>
        Dodajemy odległości: <InlineMath math={`${absA}+${absB}=${absA + absB}`} />, więc wynik to{" "}
        <InlineMath math={`${data.result}`} />.
      </span>,
    ];
  }

  if (type === "IntegerSubtractNegativePositiveTask") {
    return [
      <span>
        Dwa znaki minus obok siebie zamieniają działanie na dodawanie:{" "}
        <InlineMath math={`${data.a}-(-${absB})=${data.a}+${absB}`} />.
      </span>,
      <span>
        Obliczamy <InlineMath math={`${data.a}+${absB}=${data.result}`} />.
      </span>,
    ];
  }

  if (type === "IntegerSubtractNegativeMixedTask") {
    const rewritten = `(-${absA})+${absB}`;
    return [
      <span>
        Zamieniamy odejmowanie liczby ujemnej na dodawanie:{" "}
        <InlineMath math={`(-${absA})-(-${absB})=${rewritten}`} />.
      </span>,
      <span>
        Startujemy od <InlineMath math={`-${absA}`} /> i przesuwamy się o{" "}
        <InlineMath math={`${absB}`} /> w prawo na osi liczbowej.
      </span>,
      <span>
        <InlineMath math={`${rewritten}=${data.result}`} />.
      </span>,
    ];
  }

  return [
    <span>
      Najpierw upraszczamy znaki, np. <InlineMath math={"-( -a)=+a"} />, a potem liczymy od lewej
      do prawej: <InlineMath math={data.chainParts.join("=")} />.
    </span>,
  ];
}

export function renderExplanation({ type, data }) {
  const steps = explanationSteps(type, data);
  return (
    <div style={{ textAlign: "left" }}>
      <p>{data.note}</p>
      {steps.map((step, index) => (
        <p key={index}>{step}</p>
      ))}
      <p>
        <strong>Wynik:</strong> <InlineMath math={`${operationLatex(data)}=${data.result}`} />.
      </p>
    </div>
  );
}

const taskAdapter = {
  renderTask,
  renderInput,
  checkAnswer,
  renderAnswer,
  renderUserAnswer,
  renderExplanation,
};

export const registry = {
  IntegerStandardSubtractionTask: taskAdapter,
  IntegerNegativeSubtractionTask: taskAdapter,
  IntegerAddNegativeTask: taskAdapter,
  IntegerAddTwoNegativesTask: taskAdapter,
  IntegerSubtractNegativePositiveTask: taskAdapter,
  IntegerSubtractNegativeMixedTask: taskAdapter,
  IntegerCompositeThreeTermsTask: taskAdapter,
  IntegerCompositeFourTermsTask: taskAdapter,
};
